import {
	CheckboxField,
	CreateDrawerFormProps,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nSlateTextInputField,
	I18nTextField,
	TextField,
	UpdateDrawerFormProps,
	isEmptySlateValue,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { consentService } from "../shared/api/consentService";
import { ConsentFormValues, ConsentTranslationEntry, ConsentUpdateResource } from "../shared/types/consent";
import { ConsentTypeSelectField, defaultConsentType } from "./ConsentTypeSelectField";

const useValidate = () => {
	const { t } = useTranslation();

	return (values: ConsentFormValues) => {
		const errors: { [K in keyof ConsentFormValues]?: any } = {};
		if (!values.name) {
			errors.name = t("Required field");
		}
		if (!values.type) {
			errors.type = t("Required field");
		}

		return {
			...errors,
			...validateEachLanguage(values.translations, (_, transitions) => {
				const errors: { [K in keyof ConsentTranslationEntry]?: any } = {};
				if (isEmptySlateValue(transitions.consent)) {
					errors.consent = t("Required field");
				}

				return errors;
			}),
		};
	};
};

const emptyTranslation = { headline: "", consent: [{ type: "paragraph", children: [{ text: "" }] }] };

const ConsentFormDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
			<I18nOnlyOnMainForm>
				<ConsentTypeSelectField name="type" label={t("Type")} />
				<TextField name="name" label={t("Name")} required />
				<TextField name="duration" type="number" label={t("Duration")} />
				<CheckboxField name="requiresConfirmation" label={t("Requires confirmation")} />
			</I18nOnlyOnMainForm>
			<I18nTextField name="headline" label={t("Headline")} />
			<I18nSlateTextInputField name="consent" label={t("Consent")} required />
		</I18nFormDrawerContent>
	);
};

export const ConsentCreateDrawer: FC<CreateDrawerFormProps<ConsentFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const { languageSettings } = useDynamicConfig();
	const initialValues: ConsentFormValues = useMemo(
		() => ({
			type: defaultConsentType,
			requiresConfirmation: false,
			duration: "",
			name: "",
			translations: [
				{
					locale: languageSettings.fallback,
					headline: "",
					consent: [{ type: "paragraph", children: [{ text: "" }] }],
				},
			],
		}),
		[languageSettings.fallback],
	);

	return (
		<I18nFormDrawer
			{...props}
			initialValues={initialValues}
			title={t("New consent")}
			validate={validate}
			renderForm={(props) => <ConsentFormDrawerContent {...props} />}
		/>
	);
};

export const ConsentUpdateDrawer: FC<UpdateDrawerFormProps<ConsentUpdateResource>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<I18nFormFetchDrawer
			{...props}
			title={t("Edit consent")}
			queryFn={() => consentService.show(props.id).then((x) => x.data.data)}
			queryKey={consentService.showKey(props.id)}
			validate={validate}
			renderSuccess={(props) => <ConsentFormDrawerContent {...props} />}
		/>
	);
};
